import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { applyModeratorCompanyHostFromStorage } from '../utils/companyScope';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      applyModeratorCompanyHostFromStorage();
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/auth/me/');
      const userData = response.data;
      setUser(userData);
      if (userData?.role === 'moderator') {
        applyModeratorCompanyHostFromStorage();
      } else {
        localStorage.removeItem('moderatorCompanyHost');
        delete axios.defaults.headers.common['X-Company-Host'];
      }
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('moderatorCompanyHost');
      delete axios.defaults.headers.common['Authorization'];
      delete axios.defaults.headers.common['X-Company-Host'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login/', { email, password });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      setUser(user);
      if (user?.role === 'moderator') {
        applyModeratorCompanyHostFromStorage();
      }
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка входа'
      };
    }
  };

  const externalLogin = async (jwtToken) => {
    try {
      const response = await axios.post('/api/auth/external-login/', { token: jwtToken });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Token ${token}`;
      setUser(user);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка внешней авторизации',
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('moderatorCompanyHost');
      delete axios.defaults.headers.common['Authorization'];
      delete axios.defaults.headers.common['X-Company-Host'];
      setUser(null);
    }
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Token ${token}`;
    setUser(userData);
  };

  const value = {
    user,
    loading,
    login,
    externalLogin,
    loginWithToken,
    logout,
    isAuthenticated: !!user,
    isModerator: user?.role === 'moderator',
    isManager: user?.role === 'manager',
    isClient: user?.role === 'client',
    isModeratorOrManager: user?.role === 'moderator' || user?.role === 'manager',
    canShare: user?.can_share !== false,
    isEmbedded: !!user?.embed_mode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
